import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useOperatorContext } from './BaseLayoutAndData.tsx';
import { DEVICE_UUID } from '../../common/state/CurrentOperatorLoginState.ts';
import {
    findMatchingUrlAction,
    QrScanActionType,
} from 'dg-web-shared/common/utils/QrScanPatternMatching.tsx';
import { RecodeErrorType } from '../../cloud-connector/CloudConnectorRecodeEntryWithoutExitConditionsOutlet.tsx';
import { RELEASE_ID } from 'dg-web-shared/common/utils/release';
import { OPERATOR_EXTERNAL_ID_HEADER } from '../../utils/Utils.ts';

type QRCodeScanner = () => void;

interface NativeBridge {
    scanQRCode: QRCodeScanner | null;
    requestFCMToken: () => void;
    notificationSettings: () => void;
    appTokenV2: (
        appToken: string | null,
        uuid: string,
        version: string,
    ) => void;
}

export const getBridge = (): NativeBridge | null => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window;
    if (w.android) {
        return w.android;
    } else if (w.webkit?.messageHandlers?.appToken) {
        return {
            scanQRCode: scanQRCodeHandler(),
            requestFCMToken: getRequestFCMTokenHandler(),
            notificationSettings: getNotificationSettingsHandler(),
            appTokenV2: getAppTokenHandler(),
        };
    } else {
        return null;
    }
};

export function isRunningInNativeWrapper() {
    return Boolean(getBridge());
}

export function setAppToken(appToken: string | null) {
    const bridge = getBridge();
    if (bridge && bridge.appTokenV2) {
        bridge.appTokenV2(appToken, DEVICE_UUID, RELEASE_ID);
    }
}

function scanQRCodeHandler(): QRCodeScanner | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window;
    if (
        w.webkit &&
        w.webkit.messageHandlers &&
        w.webkit.messageHandlers.scanQRCode
    ) {
        return () => w.webkit.messageHandlers.scanQRCode.postMessage(null);
    }
    return null;
}

function getRequestFCMTokenHandler() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window;
    if (
        w.webkit &&
        w.webkit.messageHandlers &&
        w.webkit.messageHandlers.requestFCMToken
    ) {
        return () => w.webkit.messageHandlers.requestFCMToken.postMessage(null);
    }
    return () => null;
}

function getNotificationSettingsHandler() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window;
    if (
        w.webkit &&
        w.webkit.messageHandlers &&
        w.webkit.messageHandlers.notificationSettings
    ) {
        return () =>
            w.webkit.messageHandlers.notificationSettings.postMessage(null);
    }
    return () => null;
}

function getAppTokenHandler() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window;
    if (
        w.webkit &&
        w.webkit.messageHandlers &&
        w.webkit.messageHandlers.appToken
    ) {
        return (appToken: string | null, uuid: string, appVersion: string) =>
            w.webkit.messageHandlers.appToken.postMessage({
                appToken,
                uuid,
                appVersion,
            });
    }
    return () => null;
}

const enum NativeDeviceMessageType {
    FCMToken = 'FCMToken',
    PushNotificationCallback = 'PushNotificationCallback',
    QRScannerCallback = 'QRScannerCallback',
}

type MessageFromNativeDeviceCallback = (
    type: NativeDeviceMessageType,
    message: string,
) => void;

export type FcmToken = {
    token: string | null;
    status: boolean | null;
};
export type NativeContextState = {
    fcmToken: FcmToken;
};

function emptyNativeContext() {
    return {
        fcmToken: { token: null, status: null },
    };
}

const NativeContext = createContext<NativeContextState>(
    emptyNativeContext() as NativeContextState,
);

export function useNativeContext() {
    return useContext(NativeContext);
}

type WindowWithBridge = {
    messageFromNativeDevice: MessageFromNativeDeviceCallback;
} & Window &
    typeof globalThis;

export function NativeContextProdivder() {
    const [nativeState, setNativeState] = useState(emptyNativeContext);
    const { currentLogin, operatorExternalId } = useOperatorContext();
    const navigate = useNavigate();
    useEffect(() => {
        (window as WindowWithBridge).messageFromNativeDevice = (
            type: NativeDeviceMessageType,
            message: string,
        ) => {
            switch (type) {
                case NativeDeviceMessageType.FCMToken:
                    return setNativeState(prevState => ({
                        ...prevState,
                        fcmToken: JSON.parse(message),
                    }));
                case NativeDeviceMessageType.PushNotificationCallback:
                    return 'ok';
                case NativeDeviceMessageType.QRScannerCallback: {
                    const urlAction = findMatchingUrlAction(message);

                    switch (urlAction.action) {
                        case QrScanActionType.PARSING_FAILED:
                        case QrScanActionType.SCAN_PERMIT_URL:
                        case QrScanActionType.SCAN_ZONE_URL:
                        case QrScanActionType.SCAN_UNKNOWN:
                            {
                                navigate(
                                    `recode/error/${RecodeErrorType.PARSING_FAILED}`,
                                );
                            }
                            return 'ok';
                        case QrScanActionType.SCAN_TICKET_URL: {
                            const ticketBase64 =
                                urlAction.url.pathname.split('/t/')[1];
                            navigate(`recode/ticketScan/${ticketBase64}`);
                            return 'ok';
                        }
                        case QrScanActionType.SCAN_TICKET_QR_URL: {
                            const ticketId =
                                urlAction.url.pathname.split('/qrt/')[1];
                            navigate(`recode/ticketScanQr/${ticketId}`);
                            return 'ok';
                        }
                    }

                    return 'ok';
                }
                default:
                    return 'unknownMessageFromDevice';
            }
        };
    }, [navigate]);

    // A device without FCM token answers every request with a new null token. Requesting again on
    // each answer looped at up to 40 requests per second, so request once and send each value once.
    const fcmTokenRequested = useRef(false);
    const lastSentFcmToken = useRef<string | null>(null);

    useEffect(() => {
        if (currentLogin == null) {
            return;
        }
        const fcmToken = currentLogin.fcmToken;
        const fcmState = nativeState.fcmToken;
        if (fcmState.token === null && !fcmTokenRequested.current) {
            fcmTokenRequested.current = true;
            const bridge = getBridge();
            if (bridge && bridge.requestFCMToken) {
                bridge.requestFCMToken();
            }
        }

        const toSend = JSON.stringify([
            currentLogin.operatorLoginId,
            fcmState.token,
            fcmState.status,
        ]);
        if (
            fcmState.status !== null &&
            lastSentFcmToken.current !== toSend &&
            (fcmToken === null ||
                fcmState.token !== fcmToken.token ||
                fcmState.status !== fcmToken.status)
        ) {
            lastSentFcmToken.current = toSend;
            fetch('/ui-api/operator-account/fcm-token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    [OPERATOR_EXTERNAL_ID_HEADER]: operatorExternalId,
                },
                body: JSON.stringify({
                    operatorLoginDeviceId: DEVICE_UUID,
                    fcmToken: fcmState.token,
                    status: fcmState.status,
                }),
            });
        }
    }, [currentLogin, operatorExternalId, nativeState.fcmToken]);

    return (
        <NativeContext.Provider value={nativeState}>
            <Outlet />
        </NativeContext.Provider>
    );
}
